import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type {
  ResolvedConfiguration,
  PresetConfiguration,
  JsonPatchOperation,
  ConfigurationEditorState,
  PresetPatchOperation
} from '@/types/configuration'
import { PRECEDENCE_LEVELS } from '@/types/configuration'
import { configurationService, templateService } from '@/services/api'
import { withApiNotifications, API_MESSAGES } from '@/utils/api-notifications'
import { generateJsonPatchOperations, optimizeJsonPatchOperations } from '@/utils/json-patch'
import { useAuth } from '@/composables/useAuth'
import { useFieldPermissions } from '@/composables/useFieldPermissions'
// --- Variant selection constants/types ---
interface VariantRef { presetId: number; name: string }
interface LevelDef { key: string; presetTypeName: string; precedence: number; mutable: boolean }

// Hardcoded fallback for precedence levels (used if API fails)
const PRESET_LEVELS_FALLBACK: LevelDef[] = [
  { key: 'default', presetTypeName: 'default', precedence: 0, mutable: false },
  { key: 'partner', presetTypeName: 'partner', precedence: 100, mutable: true },
  { key: 'deployment', presetTypeName: 'deployment', precedence: 200, mutable: true },
  { key: 'regional', presetTypeName: 'regional', precedence: 300, mutable: true },
  { key: 'environment', presetTypeName: 'environment', precedence: 400, mutable: true },
  { key: 'feature', presetTypeName: 'feature', precedence: 500, mutable: true }
  // Note: tenant (precedence 1000) is handled separately as Tenant Overrides section
]

export const useConfigurationStore = defineStore('configuration', () => {
  // Dynamic precedence levels (loaded from API with fallback to hardcoded)
  const precedenceLevels = ref<LevelDef[]>(PRESET_LEVELS_FALLBACK)
  const PRESET_LEVELS = computed(() => precedenceLevels.value)
  
  // Load precedence levels from API
  const loadPrecedenceLevels = async () => {
    try {
      const response = await configurationService.getPrecedenceLevels()
      precedenceLevels.value = response.levels.map(l => ({
        key: l.name,
        presetTypeName: l.name,
        precedence: l.precedence,
        mutable: l.name !== 'default' // Only 'default' is immutable
      }))
      console.log(`[precedence] Loaded ${precedenceLevels.value.length} precedence levels from API`)
    } catch (error) {
      console.warn('[precedence] Failed to load precedence levels from API, using fallback', error)
      precedenceLevels.value = PRESET_LEVELS_FALLBACK
    }
  }

  // Load roles cache from API (for dynamic role lookups)
  const loadRoles = () => {
    // Delegate to permissions composable
    permissions.loadRoles()
  }
  
  // Safe clone utility to avoid DOMException: Proxy object could not be cloned
  // --- ETag normalization helper ---
  const normalizeETag = (raw?: string | null): string | undefined => {
    if (!raw) return undefined
    let e = raw.trim()
    let weak = false
    if (e.startsWith('W/')) { weak = true; e = e.slice(2) }
    // Strip all surrounding quotes (handles double-wrapped cases) once but keep inner value
    while (e.startsWith('"') && e.endsWith('"') && e.length >= 2) {
      e = e.slice(1, -1)
    }
    if (e === '*') return '*'
    return (weak ? 'W/' : '') + '"' + e + '"'
  }
  const safeClone = <T>(val: T): T => {
    try {
      // structuredClone is fastest but can throw on Proxies
      // @ts-ignore
      return typeof structuredClone === 'function' ? structuredClone(val) : JSON.parse(JSON.stringify(val))
    } catch {
      try {
        return JSON.parse(JSON.stringify(val))
      } catch {
        return val // Fallback – return original (should not mutate downstream)
      }
    }
  }
  // Initialize auth to get current user role
  const { userRole, permissions } = useAuth()
  
  // Initialize permissions for field-level access control
  const fieldPermissions = useFieldPermissions()
  
  // State
  const editorState = ref<ConfigurationEditorState>({
    selectedTenant: undefined,
    selectedPreset: undefined,
    editMode: false,
    lockState: {
      isLocked: false
    },
    isDirty: false,
    validationErrors: [],
    currentView: 'form'
  })

  const selectedPresetIndex = ref(-1) // -1 means show all presets
  const isTenantOverridesSelected = ref(false)
  const currentConfiguration = ref<ResolvedConfiguration | PresetConfiguration | null>(null)
  const originalConfiguration = ref<ResolvedConfiguration | PresetConfiguration | null>(null)
  const originalPresetConfigurations = ref<PresetConfiguration[]>([]) // Snapshot of preset configs when entering edit mode
  const pendingOperations = ref<JsonPatchOperation[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  // Track which sections are currently in edit mode (survives re-renders)
  // Key format: 'preset:{presetId}' or 'tenant'
  const sectionsInEditMode = ref<Set<string>>(new Set())
  
  // Computed: Check if any sections are currently in edit mode
  const hasAnySectionsInEditMode = computed(() => sectionsInEditMode.value.size > 0)
  
  // Track individual section view modes
  // Key format: 'preset:{presetId}' or 'tenant'
  const sectionViewModes = ref<Map<string, 'form' | 'json'>>(new Map())
  
  // Global view mode for all configuration sections (form/json toggle)
  const globalSectionViewMode = ref<'form' | 'json'>('form')
  // Counter to force sync of all sections to global view mode (incremented on repeated clicks)
  const globalSectionViewModeForceSync = ref(0)
  // Template introspection state
  const templateIntrospection = ref<any | null>(null)
  const loadingIntrospection = ref(false)
  const introspectionError = ref<string | null>(null)

  // Template resolution diagnostics (moved from preview panel to status bar)
  interface TemplateDiagnosticError {
    id: string
    message: string
    raw: string
    tokens?: string[]
  }
  const templateDiagnosticsErrors = ref<TemplateDiagnosticError[]>([])
  const templateDiagnosticsLastUpdated = ref<Date | null>(null)

  const templateDiagnosticsErrorCount = computed(() => templateDiagnosticsErrors.value.length)

  const setTemplateDiagnosticsErrors = (lines: string[] | null) => {
    if (!lines || lines.length === 0) {
      templateDiagnosticsErrors.value = []
      templateDiagnosticsLastUpdated.value = new Date()
      return
    }
    const dedup = new Set<string>()
    const parsed: TemplateDiagnosticError[] = []
    for (const line of lines) {
      const trimmed = (line || '').trim()
      if (!trimmed) continue
      if (dedup.has(trimmed)) continue
      dedup.add(trimmed)
      // Extract tokens inside single quotes for potential future features
      const tokenMatches = Array.from(trimmed.matchAll(/'([^']+)'/g)).map(m => m[1]).filter(t => t && t.length > 1)
      parsed.push({
        id: trimmed.slice(0, 80) + '::' + parsed.length,
        message: trimmed,
        raw: line,
        tokens: tokenMatches.length ? tokenMatches : undefined
      })
    }
    templateDiagnosticsErrors.value = parsed
    templateDiagnosticsLastUpdated.value = new Date()
  }
  
  // Preset configurations state
  const presetConfigurations = ref<PresetConfiguration[]>([])
  const loadingPresets = ref(false)
  const presetErrors = ref<{presetId: number, error: string}[]>([])
  
  // Track ETags per preset for optimistic concurrency control
  const presetETags = ref<Record<number, string>>({})

  // --- Variant selection state ---
  // Available variants per level (lazy loaded)
  const availableVariantsByLevel = ref<Record<string, VariantRef[]>>({})
  // Track which levels have had their full variant list loaded (vs. just seeded with selected variant)
  const fullyLoadedLevels = ref<Record<string, boolean>>({})
  // Current (possibly unsaved) selection
  const selectedVariantsByLevel = ref<Record<string, VariantRef | null>>({})
  // Committed (loaded) mapping snapshot for dirty comparison
  const committedVariantsByLevel = ref<Record<string, VariantRef | null>>({})
  // Track which levels are loading variants
  const loadingVariants = ref<Record<string, boolean>>({})
  const variantErrors = ref<Record<string, string | null>>({})
  const creatingVariant = ref<{ levelKey: string | null; inProgress: boolean; error?: string }>({ levelKey: null, inProgress: false })

  const variantDirty = computed(() => {
    return PRESET_LEVELS.value.some(l => {
      if (!l.mutable) return false
      const sel = selectedVariantsByLevel.value[l.key]?.presetId
      const com = committedVariantsByLevel.value[l.key]?.presetId
      return sel !== com
    })
  })

  // When variant selection changes in edit mode, update currentConfiguration view to reflect new mapping.
  // We don't immediately persist; this is a preview of the resolved configuration with pending variant choices.
  watch(selectedVariantsByLevel, async (newMap, oldMap) => {
    if (!editorState.value.editMode) return
    // Skip if no tenant or if still loading base configuration
    if (!editorState.value.selectedTenant) return
    // Determine if any presetId actually changed
    const changed = PRESET_LEVELS.value.some(l => (newMap[l.key]?.presetId || null) !== (oldMap?.[l.key]?.presetId || null))
    if (!changed) return
    try {
      // Derive a temporary mapping object (level -> presetId) for backend (future enhancement if API supports custom resolution)
      // For now, we simply refresh full resolved configuration; backend currently reflects committed mapping only.
      // This means until a save occurs, the resolved view will still show previous committed config.
      // To provide immediate user feedback, we optimistically update presetSummary locally.
      if (currentConfiguration.value && 'presetSummary' in currentConfiguration.value) {
        const summary = [...(currentConfiguration.value as any).presetSummary]
        PRESET_LEVELS.value.forEach(l => {
          if (!l.mutable) return
            const existingIdx = summary.findIndex((s: any) => (s.presetType || s.PresetType || '').toString().toLowerCase() === l.key)
            const sel = newMap[l.key]
            if (sel) {
              if (existingIdx >= 0) {
                summary[existingIdx] = { ...summary[existingIdx], presetId: sel.presetId, presetName: sel.name }
              } else {
                summary.push({ presetId: sel.presetId, presetName: sel.name, presetType: l.key, priority: 0, hasOverrides: false })
              }
            } else if (existingIdx >= 0) {
              summary.splice(existingIdx, 1)
            }
        })
        ;(currentConfiguration.value as any).presetSummary = summary
      }
    } catch (e) {
      if (import.meta.env.DEV) console.debug('[variant-preview] failed to apply optimistic summary update', e)
    }
  }, { deep: true })

  // Schema state
  const availableSchemas = ref<any[]>([])
  const presetSchemas = ref<Record<string, any>>({})
  const loadingSchemas = ref(false)
  const schemaErrors = ref<{presetId: string, error: string}[]>([])

  // Computed
  const isEditMode = computed(() => editorState.value.editMode)
  const isLocked = computed(() => editorState.value.lockState.isLocked)
  const isDirty = computed(() => editorState.value.isDirty)
  const hasValidationErrors = computed(() => editorState.value.validationErrors.length > 0)
  
  // Computed for successfully loaded presets count
  const activePresetsCount = computed(() => presetConfigurations.value.length)
  const hasPresetErrors = computed(() => presetErrors.value.length > 0)
  
  const presetHierarchy = computed(() => {
    if (!currentConfiguration.value) return []
    
    // Handle both ResolvedConfiguration and PresetConfiguration types
    if ('presetSummary' in currentConfiguration.value) {
      // This is a ResolvedConfiguration with presetSummary
      return currentConfiguration.value.presetSummary
        .slice()
        .sort((a, b) => a.precedence - b.precedence)
    } else if ('presetInfo' in currentConfiguration.value) {
      // This is a PresetConfiguration with presetInfo
      const presetConfig = currentConfiguration.value as PresetConfiguration
      return [{
        presetId: presetConfig.presetInfo.presetId,
        presetName: presetConfig.presetInfo.presetName,
        presetType: presetConfig.presetInfo.presetType,
        precedence: 0, // Default precedence for single preset view
        hasOverrides: presetConfig.presetInfo.hasTenantOverrides
      }]
    }
    
    return []
  })

  const configurationJson = computed(() => {
    if (!currentConfiguration.value) return ''
    
    // Handle both ResolvedConfiguration and PresetConfiguration types
    if ('configuration' in currentConfiguration.value) {
      // This is a ResolvedConfiguration
      return JSON.stringify(currentConfiguration.value.configuration, null, 2)
    } else if ('effectiveConfiguration' in currentConfiguration.value) {
      // This is a PresetConfiguration - use effectiveConfiguration
      return JSON.stringify(currentConfiguration.value.effectiveConfiguration, null, 2)
    }
    
    return ''
  })

  // Permission-related computed properties
  // Check if user has ANY write permission for presets (blanket or path-specific)
  const canEditPresets = computed(() => {
    // Check for blanket permission first
    if (permissions.canWrite('presets')) return true
    
    // Check if user has any path-specific write permission for presets
    const userScopes = permissions.scopes.value
    return userScopes.some((scope: string) => 
      scope.startsWith('presets:write:') || 
      scope === 'presets:write'
    )
  })
  
  // Check if user has ANY write permission for tenants (blanket or path-specific)
  const canEditTenants = computed(() => {
    // Check for blanket permission first
    if (permissions.canWrite('tenants')) return true
    
    // Check if user has any path-specific write permission for tenants
    const userScopes = permissions.scopes.value
    return userScopes.some((scope: string) => 
      scope.startsWith('tenants:write:') || 
      scope === 'tenants:write'
    )
  })
  
  const canEditCurrentResource = computed(() => {
    const resource = isTenantOverridesSelected.value ? 'tenants' : 'presets'
    return permissions.canWrite(resource)
  })
  const isReadOnlyMode = computed(() => {
    const resource = isTenantOverridesSelected.value ? 'tenants' : 'presets'
    return permissions.isReadOnlyAccess(resource)
  })

  // Actions
  const setSelectedTenant = async (tenantId: string) => {
    if (editorState.value.editMode) {
      throw new Error('Cannot change tenant while in edit mode')
    }
    
    editorState.value.selectedTenant = tenantId
    editorState.value.selectedPreset = undefined
    
    if (tenantId) {
      await loadConfiguration()
    } else {
      currentConfiguration.value = null
      originalConfiguration.value = null
    }
  }

  const setSelectedPreset = async (presetId?: number) => {
    if (editorState.value.editMode) {
      throw new Error('Cannot change preset while in edit mode')
    }
    
    editorState.value.selectedPreset = presetId
    await loadConfiguration()
  }

  const loadConfiguration = async () => {
    if (!editorState.value.selectedTenant) return
    
    loading.value = true
    error.value = null
    
    // Reset variant loading state for new tenant
    fullyLoadedLevels.value = {}
    
    try {
      await withApiNotifications(
        async () => {
          // Always fetch the resolved tenant configuration (includes all preset levels)
          const config = await configurationService.getResolvedConfiguration(
            editorState.value.selectedTenant!
          )
          
          currentConfiguration.value = config
          originalConfiguration.value = structuredClone(config)
          editorState.value.isDirty = false
          pendingOperations.value = []

          // Initialize variant mapping from presetSummary (if available)
          if (config && 'presetSummary' in config) {
            const summary: any[] = (config as any).presetSummary || []
            const tempSelected: Record<string, VariantRef | null> = {}
            PRESET_LEVELS.value.forEach(l => {
              const match = summary.find(s => (s.presetType || s.PresetType)?.toString?.().toLowerCase?.() === l.key || s.presetType === l.key || (s.presetType == null && s.PresetType === l.key))
              // Fallback: match by type name property variations
              const byName = summary.find(s => (s.presetType || s.PresetType || s.presetTypeName || s.PresetTypeName)?.toString?.().toLowerCase?.() === l.key)
              const chosen = match || byName
              if (chosen) {
                const pid = chosen.presetId ?? chosen.PresetId
                const pname = chosen.presetName ?? chosen.PresetName
                const variant = { presetId: pid, name: pname }
                tempSelected[l.key] = variant
                // Seed availableVariantsByLevel with the selected variant so dropdown can display it immediately
                // (Full list will be loaded lazily when dropdown is opened)
                if (!availableVariantsByLevel.value[l.key]) {
                  availableVariantsByLevel.value[l.key] = [variant]
                } else if (!availableVariantsByLevel.value[l.key].some(v => v.presetId === pid)) {
                  availableVariantsByLevel.value[l.key].push(variant)
                }
              } else {
                tempSelected[l.key] = null
              }
            })
            selectedVariantsByLevel.value = tempSelected
            committedVariantsByLevel.value = structuredClone(tempSelected)
          } else {
            // Clear mapping if not resolved config
            selectedVariantsByLevel.value = {}
            committedVariantsByLevel.value = {}
          }
          
                    // Load preset configurations immediately after loading the main configuration
          // This ensures we use the fresh presetSummary from the new tenant
          if (config && 'presetSummary' in config && config.presetSummary.length > 0) {
            // IMPORTANT: Await this to ensure ETags are loaded before edit mode can be entered
            await loadPresetConfigurations()
            
            // Load schemas after preset configurations are loaded
            Promise.all([
              loadAvailableSchemas(),
              loadPresetSchemas()
            ]).catch((error) => {
              console.warn('Failed to load schemas after preset config load:', error)
            })
          }
          
          // After configuration loads, trigger a background template diagnostics resolution.
          queueMicrotask(() => {
            resolveTemplateDiagnostics().catch(err => {
              if (import.meta.env.DEV) console.debug('[diagnostics] background resolve failed', err)
            })
          })
          return config
        },
        {
          loadingMessage: API_MESSAGES.LOADING.GET_CONFIGURATION,
          successMessage: API_MESSAGES.SUCCESS.GET_CONFIGURATION,
          errorMessage: API_MESSAGES.ERROR.GET_CONFIGURATION,
          showSuccess: false // Don't show success for routine loading
        }
      )
    } catch (err: any) {
      error.value = err.message || 'Failed to load configuration'
      console.error('Configuration load error:', err)
    } finally {
      loading.value = false
    }
  }

  // Lightweight template resolution to populate diagnostics without building substitution diff map.
  const resolveTemplateDiagnostics = async () => {
    if (!currentConfiguration.value || !editorState.value.selectedTenant) return
    console.log('[resolveTemplateDiagnostics] Starting diagnostics resolution')
    try {
      // Extract effective configuration object similar to workspace logic
      const effective = 'configuration' in currentConfiguration.value
        ? currentConfiguration.value.configuration
        : (currentConfiguration.value as any).effectiveConfiguration ?? (currentConfiguration.value as any).baseConfiguration
      console.log('[resolveTemplateDiagnostics] Testing with config:', effective)
      const response = await templateService.testTemplateSubstitution({
        jsonConfig: JSON.stringify(effective),
        maxDepth: 10
      })
      console.log('[resolveTemplateDiagnostics] Response:', response)
      // Parse errors irrespective of success flag
      let lines: string[] | null = null
      if (response && response.errors && response.errors.length) {
        lines = response.errors.map((e: any) => e.message || String(e)).filter(Boolean)
      } else if (!response.success) {
        // Fallback generic failure
        lines = ['Template resolution failed']
      } else {
        // No errors: clear existing
        lines = null
      }
      console.log('[resolveTemplateDiagnostics] Setting errors:', lines)
      setTemplateDiagnosticsErrors(lines)
      console.log('[resolveTemplateDiagnostics] Error count after update:', templateDiagnosticsErrorCount.value)
    } catch (e: any) {
      console.error('[resolveTemplateDiagnostics] Error:', e)
      setTemplateDiagnosticsErrors([e.message || 'Template diagnostics request failed'])
    }
  }

  const loadPresetConfigurations = async () => {
    if (!editorState.value.selectedTenant || !presetHierarchy.value.length || loadingPresets.value) {
      return // Already loading or missing required data
    }
    
  // Verbose preset loading logs removed
    
    loadingPresets.value = true
    presetErrors.value = []
    const configurations: PresetConfiguration[] = []
    
    try {
      // Load each preset individually and handle failures gracefully
      for (const preset of presetHierarchy.value) {
        try {
          // Load preset from global endpoint (cacheable, tenant-independent)
          const globalPreset = await configurationService.getPreset(preset.presetId.toString()) as any
          
          // Handle both old 'priority' and new 'precedence' field names from backend
          // Also fallback to type-based lookup if neither is provided
          const precedenceValue = preset.precedence ?? preset.priority ?? PRECEDENCE_LEVELS[preset.presetType as keyof typeof PRECEDENCE_LEVELS] ?? 0
          
          // Transform the simple preset response into PresetConfiguration format
          const presetConfig: PresetConfiguration = {
            presetInfo: {
              presetId: globalPreset.id,
              presetName: globalPreset.name,
              presetType: globalPreset.presetType,
              precedence: precedenceValue,
              description: globalPreset.description, // Add description from API
              hasTenantOverrides: false // Individual presets don't have tenant overrides
            },
            baseConfiguration: JSON.parse(globalPreset.json || '{}'),
            tenantOverrides: {},
            inheritedConfiguration: {},
            effectiveConfiguration: JSON.parse(globalPreset.json || '{}'),
            templateVariables: [],
            changesSummary: {
              addedProperties: [],
              modifiedProperties: [],
              removedProperties: [],
              conflictsResolved: []
            },
            resolvedAt: new Date().toISOString(),
            eTag: globalPreset.eTag || ''
          }
          
          configurations.push(presetConfig)
          
          // Store the normalized ETag for this preset for later PATCH operations
          presetETags.value[preset.presetId] = normalizeETag(globalPreset.eTag) as string
          
          // Removed success ETag detail logs
          
        } catch (error: any) {
          // Log the error but don't fail the entire operation
          console.warn(`❌ Failed to load preset ${preset.presetId} (${preset.presetName}):`, error)
          console.warn('Error details:', {
            status: error.status,
            message: error.message,
            response: error.response
          })
          presetErrors.value.push({
            presetId: preset.presetId,
            error: error.message || 'Failed to load preset'
          })
        }
      }
      
      presetConfigurations.value = configurations
      
      // Only show error notification if ALL presets failed
      if (configurations.length === 0 && presetHierarchy.value.length > 0) {
        throw new Error('Failed to load any preset configurations')
      }
      
    } catch (err: any) {
      console.error('Failed to load preset configurations:', err)
      throw err // Re-throw for notification handling
    } finally {
      loadingPresets.value = false
    }
  }

  const loadAvailableSchemas = async () => {
    if (loadingSchemas.value) return // Already loading
    
    loadingSchemas.value = true
    
    try {
      const schemas = await configurationService.getAllSchemas()
      availableSchemas.value = schemas
    } catch (error: any) {
      console.warn('Failed to load available schemas:', error)
      schemaErrors.value.push({
        presetId: 'all',
        error: error.message || 'Failed to load available schemas'
      })
    } finally {
      loadingSchemas.value = false
    }
  }

  const loadPresetSchemas = async () => {
    if (!presetHierarchy.value.length || loadingSchemas.value) {
      return // Already loading or no presets
    }
    
    loadingSchemas.value = true
    schemaErrors.value = []
    const schemas: Record<string, any> = {}
    
    try {
      // Load schemas for each preset individually
      for (const preset of presetHierarchy.value) {
        try {
          const presetSchema = await configurationService.getPresetSchemas(preset.presetId.toString())
          schemas[preset.presetId.toString()] = presetSchema
        } catch (error: any) {
          console.warn(`Failed to load schema for preset ${preset.presetId}:`, error)
          schemaErrors.value.push({
            presetId: preset.presetId.toString(),
            error: error.message || 'Failed to load preset schema'
          })
        }
      }
      
      presetSchemas.value = schemas
      
    } catch (err: any) {
      console.error('Failed to load preset schemas:', err)
      // Don't throw - schema loading is optional for basic functionality
    } finally {
      loadingSchemas.value = false
    }
  }

  const enterEditMode = async () => {
    if (!currentConfiguration.value) {
      throw new Error('No configuration loaded')
    }
    
    if (editorState.value.lockState.isLocked) {
      throw new Error('Configuration is already locked')
    }
    
    // Check permissions before allowing edit mode
    // Allow both blanket permissions (e.g., "presets:write") and path-specific permissions (e.g., "presets:write:database")
    const resource = isTenantOverridesSelected.value ? 'tenants' : 'presets'
    const hasBlanketWrite = permissions.canWrite(resource)
    const hasPathSpecificWrite = permissions.scopes.value.some(scope => {
      const parts = scope.split(':')
      if (parts.length < 2) return false
      const [scopeResource, scopeOperation] = parts
      return (scopeResource === resource || scopeResource === '*') &&
             (scopeOperation === 'write' || scopeOperation === '*')
    })
    
    if (!hasBlanketWrite && !hasPathSpecificWrite) {
      throw new Error(`You do not have permission to edit ${resource}`)
    }
    
    // Ensure preset configurations are loaded before allowing edit mode
    if (presetHierarchy.value.length > 0 && presetConfigurations.value.length === 0) {
  // Removed edit-mode lazy loading log
      await loadPresetConfigurations()
    }
    
    // Verify that we have ETags for all presets that will be edited
    for (const preset of presetHierarchy.value) {
      if (!presetETags.value[preset.presetId]) {
        console.warn(`⚠️ Missing ETag for preset ${preset.presetId}, attempting to reload...`)
        await loadPresetConfigurations()
        break
      }
    }
    
    // Preload all variant options to prevent dropdowns from closing when options are loaded on first open
    await loadAllVariants()
    
    // Store snapshot of preset configurations for comparison when detecting redundant operations
    originalPresetConfigurations.value = safeClone(presetConfigurations.value)
    
    // Acquire edit lock using ETag
    editorState.value.lockState = {
      isLocked: true,
      lockHolder: `${userRole.value}-user`, // Use auth role instead of hardcoded value
      lockAcquiredAt: new Date().toISOString(),
      eTag: normalizeETag((currentConfiguration.value as any).eTag)
    }
    
    
    editorState.value.editMode = true
    // Merge variant dirty state with existing dirty tracking
    if (variantDirty.value) editorState.value.isDirty = true
  }

  const exitEditMode = async (save: boolean = false) => {
    if (!editorState.value.editMode) return
    
    if (save && editorState.value.isDirty) {
      await saveChanges()
    }
    
    // Release lock
    editorState.value.lockState = {
      isLocked: false
    }
    
    editorState.value.editMode = false
    editorState.value.isDirty = false
    pendingOperations.value = []
    originalPresetConfigurations.value = [] // Clear snapshot
    sectionsInEditMode.value.clear() // Clear all section edit states
    
    // Reload configuration
    await loadConfiguration()
  }

  /**
   * Update a section's view mode and sync global view mode if all sections match
   * @param sectionKey - Section identifier (e.g., 'preset:123' or 'tenant')
   * @param viewMode - The view mode ('form' or 'json')
   */
  const updateSectionViewMode = (sectionKey: string, viewMode: 'form' | 'json') => {
    sectionViewModes.value.set(sectionKey, viewMode)
    
    // Check if all sections now have the same view mode
    if (sectionViewModes.value.size > 0) {
      const modes = Array.from(sectionViewModes.value.values())
      const allSame = modes.every(mode => mode === modes[0])
      
      if (allSame && modes[0] !== globalSectionViewMode.value) {
        // All sections have the same view mode, update global to match
        globalSectionViewMode.value = modes[0]
      }
    }
  }

  /**
   * Remove a section's view mode tracking (e.g., when section is unmounted)
   * @param sectionKey - Section identifier to remove
   */
  const removeSectionViewMode = (sectionKey: string) => {
    sectionViewModes.value.delete(sectionKey)
  }

  const updateConfiguration = (newConfig: any) => {
    if (!editorState.value.editMode) {
      throw new Error('Cannot update configuration outside of edit mode')
    }
    
    if (currentConfiguration.value) {
      // Handle both ResolvedConfiguration and PresetConfiguration types
      if ('configuration' in currentConfiguration.value) {
        // This is a ResolvedConfiguration
        currentConfiguration.value.configuration = newConfig
      } else if ('effectiveConfiguration' in currentConfiguration.value) {
        // This is a PresetConfiguration - update effectiveConfiguration
        currentConfiguration.value.effectiveConfiguration = newConfig
      }
      
      editorState.value.isDirty = true
      
      // Generate JSON Patch operations for the change
      if (originalConfiguration.value) {
        const originalConfig = 'configuration' in originalConfiguration.value 
          ? originalConfiguration.value.configuration 
          : originalConfiguration.value.effectiveConfiguration
        
        const currentConfig = 'configuration' in currentConfiguration.value 
          ? currentConfiguration.value.configuration 
          : currentConfiguration.value.effectiveConfiguration
        
        // Generate patch operations
        const newOperations = generateJsonPatchOperations(originalConfig, currentConfig)
        
        // Optimize and add to pending operations
        pendingOperations.value = optimizeJsonPatchOperations([...pendingOperations.value, ...newOperations])
      }
    }
  }

  // Helper function to get the original value at a JSON pointer path
  const getOriginalValueAtPath = (path: string, presetContext?: { presetId: number }): any => {
    try {
      // Find the original configuration for this context
      let sourceConfig: any = null
      
      if (presetContext) {
        // Get original preset configuration from snapshot taken at edit mode entry
        const originalPresetConfig = originalPresetConfigurations.value.find(
          p => p.presetInfo.presetId === presetContext.presetId
        )
        if (originalPresetConfig) {
          sourceConfig = originalPresetConfig.baseConfiguration
        }
      } else {
        // Get original tenant overrides from resolved configuration
        if (originalConfiguration.value && 'presetSummary' in originalConfiguration.value) {
          // For tenant overrides, we need to check the original tenant overrides
          // This is stored in the resolved configuration's tenant data
          sourceConfig = (originalConfiguration.value as any).configuration || {}
        }
      }
      
      if (!sourceConfig) return undefined
      
      // Parse JSON pointer path (e.g., "/property/nested/key" -> ["property", "nested", "key"])
      const pathParts = path.split('/').filter(p => p !== '')
      
      let current = sourceConfig
      for (const part of pathParts) {
        if (current === null || current === undefined) return undefined
        if (typeof current !== 'object') return undefined
        
        // Handle array indices
        const arrayIndex = parseInt(part, 10)
        if (!isNaN(arrayIndex) && Array.isArray(current)) {
          current = current[arrayIndex]
        } else {
          current = current[part]
        }
      }
      
      return current
    } catch (error) {
      console.warn(`[getOriginalValueAtPath] Failed to get original value at ${path}:`, error)
      return undefined
    }
  }

  const addPatchOperation = (
    operation: JsonPatchOperation, 
    presetContext?: {
      presetId: number
      presetName: string
      presetType: string
    }
  ) => {
    if (!editorState.value.editMode) {
      throw new Error('Cannot add patch operation outside of edit mode')
    }
    
    // Validate preset context if provided
    if (presetContext) {
      const presetExists = presetConfigurations.value.some(p => p.presetInfo.presetId === presetContext.presetId)
      if (!presetExists) {
        console.error(`❌ Cannot add operation for preset ${presetContext.presetId}: preset was not successfully loaded`)
        throw new Error(`Cannot modify preset ${presetContext.presetName}: preset configuration is not available`)
      }
      
      const hasETag = presetETags.value[presetContext.presetId]
      if (!hasETag) {
        console.error(`❌ Cannot add operation for preset ${presetContext.presetId}: no ETag available`)
        throw new Error(`Cannot modify preset ${presetContext.presetName}: preset is not ready for editing`)
      }
    }
    
    // Check if this operation would result in the original value
    // If so, we should remove any pending operation for this path instead of adding a new one
    if (operation.op === 'replace' || operation.op === 'add') {
      const originalValue = getOriginalValueAtPath(operation.path, presetContext)
      const newValue = operation.value
      
      // Deep equality check
      const isEqualToOriginal = JSON.stringify(originalValue) === JSON.stringify(newValue)
      
      if (isEqualToOriginal) {
        // Remove any existing operation for this path since we're reverting to original
        const existingIndex = pendingOperations.value.findIndex(
          op => op.path === operation.path && 
                op.presetContext?.presetId === presetContext?.presetId
        )
        
        if (existingIndex !== -1) {
          pendingOperations.value.splice(existingIndex, 1)
          console.log(`[addPatchOperation] Removed redundant operation for ${operation.path} (reverted to original value)`)
        }
        
        // Update dirty state
        editorState.value.isDirty = pendingOperations.value.length > 0 || variantDirty.value
        
        // Still apply the patch to update the UI to show the original value
        if (presetContext) {
          applyPatchToPresetConfigurations(operation, presetContext)
        } else {
          applyPatchToTenantConfiguration(operation)
        }
        
        return // Don't add the operation
      }
    }
    
    // Store preset context with the operation for later use during save
    // Deep clone the operation value to prevent reference mutation issues
    // (especially important for arrays/objects that get modified by optimistic patches)
    const operationWithContext = {
      ...operation,
      value: operation.value !== undefined ? structuredClone(operation.value) : undefined,
      presetContext
    }
    
    // Deduplicate operations - if there's already an operation for the same path AND same preset context,
    // replace it instead of adding a new one
    // EXCEPTION: Don't deduplicate array append operations (path ends with '/-') as each represents a distinct item
    const isArrayAppend = operation.path.endsWith('/-')
    const existingIndex = !isArrayAppend ? pendingOperations.value.findIndex(
      op => op.path === operation.path && 
            op.op === operation.op &&
            op.presetContext?.presetId === presetContext?.presetId
    ) : -1
    
    if (existingIndex !== -1) {
      // Replace existing operation for the same path and preset context
      pendingOperations.value[existingIndex] = operationWithContext
    } else {
      // Add new operation
      pendingOperations.value.push(operationWithContext)
    }
    
    editorState.value.isDirty = true
    
    // Apply the patch optimistically for immediate UI feedback
    if (presetContext) {
      // Apply to preset configurations
      applyPatchToPresetConfigurations(operation, presetContext)
    } else {
      // Apply to tenant configuration (for paths starting with /tenant/)
      applyPatchToTenantConfiguration(operation)
    }
  }
  
  // Helper function to apply a patch operation to preset configurations
  const applyPatchToPresetConfigurations = (
    operation: JsonPatchOperation,
    presetContext?: {
      presetId: number
      presetName: string
      presetType: string
    }
  ) => {
    try {
      // Parse the path to determine which preset and property to update
      const pathParts = operation.path.split('/')
      if (pathParts.length < 2) {
        console.warn('⚠️ Configuration Store: Invalid path format:', operation.path)
        return
      }
      
      // Find the correct preset to apply the patch to
      let targetPreset
      if (presetContext) {
        targetPreset = presetConfigurations.value.find(p => p.presetInfo.presetId === presetContext.presetId)
        if (!targetPreset) {
          console.warn(`⚠️ Configuration Store: Preset ${presetContext.presetId} not found for patch application`)
          return
        }
      } else {
        // Fallback to first preset if no context provided
        targetPreset = presetConfigurations.value[0]
      }
      
      if (targetPreset && targetPreset.baseConfiguration) {
        // Apply the patch to the target preset's baseConfiguration
        applyJsonPatchOperation(targetPreset.baseConfiguration, operation)
  // Removed optimistic patch application log
      } else {
        console.warn('⚠️ Configuration Store: No baseConfiguration found in target preset')
      }
    } catch (error) {
      console.warn('❌ Configuration Store failed to apply patch optimistically:', error)
    }
  }
  
  // Simple JSON patch operation applier
  const applyJsonPatchOperation = (target: any, operation: JsonPatchOperation) => {
    if (!target) {
      if (import.meta.env.DEV) {
        console.warn('❌ Cannot apply patch operation: target is null/undefined')
      }
      return
    }
    
    const rawPath = operation.path.startsWith('/') ? operation.path.slice(1) : operation.path
    const parts = rawPath === '' ? [] : rawPath.split('/')
    if (operation.op === 'add' || operation.op === 'replace') {
      if (parts.length === 0) return // root replacement not supported here
      let current = target
      for (let i = 0; i < parts.length - 1; i++) {
        const seg = parts[i]
        if (!(seg in current) || current[seg] == null) {
          // create intermediate container as object
          current[seg] = {}
        }
        current = current[seg]
      }
      const last = parts[parts.length - 1]
      if (last === '-') {
        // array append
        if (!Array.isArray(current)) return
        current.push(operation.value)
      } else if (Array.isArray(current)) {
        const index = parseInt(last, 10)
        if (!isNaN(index)) {
          // replace or insert at index
            current[index] = operation.value
        }
      } else {
        current[last] = operation.value
      }
    } else if (operation.op === 'remove') {
      if (parts.length === 0) return // root removal not supported
      let current = target
      for (let i = 0; i < parts.length - 1; i++) {
        const seg = parts[i]
        if (!(seg in current)) {
          if (import.meta.env.DEV) console.debug('[config:remove] path segment missing', seg, 'in', parts)
          return
        }
        current = current[seg]
        if (current == null) return
      }
      const last = parts[parts.length - 1]
      if (Array.isArray(current)) {
        const index = last === '-' ? current.length - 1 : parseInt(last, 10)
        if (!isNaN(index) && index >= 0 && index < current.length) {
          current.splice(index, 1)
          if (import.meta.env.DEV) console.debug('[config:remove] removed array index', index, 'remaining', current.length)
        }
      } else if (typeof current === 'object' && current !== null) {
        if (last in current) {
          delete current[last]
          if (import.meta.env.DEV) console.debug('[config:remove] deleted property', last)
        }
      }
    }
  }

  // Helper function to apply a patch operation to tenant configuration
  const applyPatchToTenantConfiguration = (operation: JsonPatchOperation) => {
    if (!currentConfiguration.value) return
    
    try {
      // For tenant operations, we expect paths relative to the tenant configuration root
      // (e.g., '/logging' not '/tenant/configuration/logging')
      
      // Ensure the current configuration has tenant configuration structure
      if (!('tenantConfiguration' in currentConfiguration.value)) {
        // Add tenant configuration structure if it doesn't exist
        ;(currentConfiguration.value as any).tenantConfiguration = {}
      }
      
      const tenantConfig = (currentConfiguration.value as any).tenantConfiguration
      
      // Ensure tenant configuration exists before applying patches
      if (!tenantConfig) {
        if (import.meta.env.DEV) {
          console.warn('❌ Cannot apply tenant patch: tenantConfiguration is null/undefined')
        }
        return
      }
      
      // Apply the patch directly to the tenant configuration
      applyJsonPatchOperation(tenantConfig, operation)
      
      if (import.meta.env.DEV) {
        console.debug('[config:tenant] Applied optimistic patch:', operation.path, '→', operation.value)
      }
    } catch (error) {
      console.warn('❌ Configuration Store failed to apply tenant patch optimistically:', error)
    }
  }

  const saveChanges = async () => {
    if (!editorState.value.editMode) {
      throw new Error('Cannot save: not in edit mode')
    }
    // Allow saves that consist solely of variant (preset assignment) changes
    if (pendingOperations.value.length === 0 && !variantDirty.value) {
      throw new Error('No pending operations to save')
    }
    
    loading.value = true
    error.value = null
    
    try {
      await withApiNotifications(
        async () => {
          // Before grouping existing patch operations, append variant mapping diffs as patch ops (tenant-level)
          // Path strategy for Schema V3: /tenant/presets/<levelKey> holding numeric presetId
          
          // Get current server-side preset assignments from tenant JSON structure
          const currentConfig = currentConfiguration.value
          const configData = currentConfig && 'configuration' in currentConfig
            ? currentConfig.configuration
            : null

          const tenantNode = configData && typeof (configData as any).tenant === 'object' && (configData as any).tenant !== null
            ? (configData as any).tenant as Record<string, unknown>
            : undefined

          // In Schema V3, presets are stored in tenant.presets, not at root level
          const serverPresetAssignments = tenantNode && typeof tenantNode.presets === 'object' && tenantNode.presets !== null
            ? (tenantNode.presets as Record<string, number>)
            : {}
          
          if (import.meta.env.DEV) {
            console.debug('[saveChanges] Server preset assignments (Schema V3):', serverPresetAssignments)
            console.debug('[saveChanges] Config data:', configData)
          }
          
          const variantOps: JsonPatchOperation[] = []
          PRESET_LEVELS.value.forEach(l => {
            if (!l.mutable) return
            const committed = committedVariantsByLevel.value[l.key]?.presetId
            const selected = selectedVariantsByLevel.value[l.key]?.presetId
            const serverValue = serverPresetAssignments[l.key]
            
            if (selected !== committed) {
              if (import.meta.env.DEV) {
                console.debug(`[saveChanges] Level ${l.key}: committed=${committed}, selected=${selected}, serverValue=${serverValue}`)
              }
              
              // Determine op type
              // Use 'replace' if we have a committed value (meaning it was previously set)
              // or if a serverValue exists, otherwise use 'add'
              const shouldReplace = committed !== undefined || serverValue !== undefined
              
              if (selected == null) {
                // Removing the assignment
                variantOps.push({ op: 'remove', path: `/tenant/presets/${l.key}` })
              } else if (shouldReplace) {
                // Path exists or was previously set, use 'replace'
                variantOps.push({ op: 'replace', path: `/tenant/presets/${l.key}`, value: selected })
              } else {
                // Brand new assignment, use 'add'
                variantOps.push({ op: 'add', path: `/tenant/presets/${l.key}`, value: selected })
              }
            }
          })
          if (variantOps.length) {
            // Dedupe keeping last occurrence per path (iterate from end)
            const seen = new Set<string>()
            const dedupReversed: JsonPatchOperation[] = []
            for (let i = variantOps.length - 1; i >= 0; i--) {
              const p = variantOps[i].path
              if (!seen.has(p)) {
                seen.add(p)
                dedupReversed.push(variantOps[i])
              }
            }
            dedupReversed.reverse()
            // Check if tenant.presets already exists in current configuration
            const currentConfig = currentConfiguration.value
            const configData = currentConfig && 'configuration' in currentConfig
              ? currentConfig.configuration
              : null
            const tenantNode = configData && typeof (configData as any).tenant === 'object' && (configData as any).tenant !== null
              ? (configData as any).tenant as Record<string, unknown>
              : undefined
            const presetsExists = tenantNode && typeof tenantNode.presets === 'object' && tenantNode.presets !== null
            
            if (import.meta.env.DEV) {
              console.debug('[saveChanges] tenant.presets exists?', presetsExists)
              console.debug('[saveChanges] Deduped variant ops:', dedupReversed)
            }
            
            // Only prepend root container add if tenant.presets doesn't exist yet
            // This avoids redundant operations that some API implementations reject
            variantOps.length = 0
            if (!presetsExists) {
              const rootOp: JsonPatchOperation = { op: 'add', path: '/tenant/presets', value: {} }
              variantOps.push(rootOp)
            }
            variantOps.push(...dedupReversed)
            
            if (import.meta.env.DEV) {
              console.debug('[saveChanges] Final variant ops to send:', variantOps)
            }
          }
          if (variantOps.length) {
            // Push variant ops (no presetContext => tenant-level) before existing pending ones so dedupe logic later can work if paths overlap
            pendingOperations.value = [...variantOps, ...pendingOperations.value]
          }
          // Group operations by type (preset vs tenant)
          const presetOperations = pendingOperations.value.filter(op => op.presetContext)
          const tenantOperations = pendingOperations.value.filter(op => !op.presetContext)
          
          let finalResult: any = null
          
          // Process preset operations if any
          if (presetOperations.length > 0) {
            // Removed preset operations processing log
            
            // Group preset operations by presetId
            const groupedByPreset = presetOperations.reduce((groups, op) => {
              const presetId = op.presetContext!.presetId
              if (!groups[presetId]) {
                groups[presetId] = []
              }
              // Remove presetContext from the operation for the API call
              const { presetContext, ...cleanOp } = op
              groups[presetId].push(cleanOp)
              return groups
            }, {} as Record<number, JsonPatchOperation[]>)
            
            // Apply patches to each preset
            for (const [presetId, operations] of Object.entries(groupedByPreset)) {
              const presetIdNum = parseInt(presetId)
              const presetETag = presetETags.value[presetIdNum]
              
              // Validate that this preset was successfully loaded
              const presetExists = presetConfigurations.value.some(p => p.presetInfo.presetId === presetIdNum)
              if (!presetExists) {
                console.error(`❌ Cannot patch preset ${presetIdNum}: preset was not successfully loaded`)
                throw new Error(`Cannot save changes to preset ${presetIdNum}: preset configuration was not loaded successfully`)
              }
              
              if (!presetETag) {
                console.error(`❌ Cannot patch preset ${presetIdNum}: no ETag available`)
                throw new Error(`Cannot save changes to preset ${presetIdNum}: no ETag available for optimistic concurrency`)
              }
              
              // DEBUG: Log what we're about to send
              console.log(`🔵 PATCH /api/preset/${presetIdNum}:`, JSON.stringify(operations, null, 2))
              
              const result = await configurationService.applyPresetPatch(
                presetIdNum,
                operations,
                presetETag  // Use preset-specific ETag
              )
              
              if (!result.success) {
                throw new Error(`Preset ${presetId} patch failed: ${result.errors?.[0]?.message || 'Unknown error'}`)
              }
              
              // Update the stored ETag for this preset
              if (result.eTag) {
                presetETags.value[presetIdNum] = result.eTag
              }
              
              finalResult = result
            }
          }
          
          // Process tenant operations if any
          if (tenantOperations.length > 0 && editorState.value.selectedTenant) {
            if (import.meta.env.DEV) {
              console.log('🔵 Sending tenant operations:', tenantOperations)
              console.log('🔵 Tenant:', editorState.value.selectedTenant)
            }
            
            const result = await configurationService.applyPatch(
              editorState.value.selectedTenant,
              tenantOperations,
              editorState.value.lockState.eTag
            )
            
            // Check for preset assignment operation failures
            if (result.presetOperations && result.presetOperations.length > 0) {
              const failedOps = result.presetOperations.filter((op: PresetPatchOperation) => !op.success)
              if (failedOps.length > 0) {
                console.error('❌ Preset assignment operations failed:', failedOps)
                const errorMessages = failedOps.map((op: PresetPatchOperation) => 
                  `${op.operation.path}: ${op.presetName === 'assignment-error' ? 'Assignment error' : op.presetName}`
                ).join(', ')
                throw new Error(`Preset assignment failed: ${errorMessages}`)
              }
            }
            
            if (!result.success) {
              throw new Error(result.message || 'Tenant patch failed')
            }
            
            if (result.configuration) {
              currentConfiguration.value = result.configuration
              originalConfiguration.value = safeClone(result.configuration)
            }
            
            finalResult = result
          }
          
          // Clear pending operations and update state
          pendingOperations.value = []
          editorState.value.isDirty = false
          // Commit variant selection snapshot (use safeClone to avoid Proxy clone errors)
          committedVariantsByLevel.value = safeClone(selectedVariantsByLevel.value)
          
          // Update ETag for continued editing
          if (finalResult?.eTag) {
            editorState.value.lockState.eTag = finalResult.eTag
          }
          
          // Reload preset configurations to reflect changes
          if (presetOperations.length > 0) {
            await loadPresetConfigurations()
          }
          // Return a plain, minimized summary object to avoid leaking reactive/proxy refs
          const plainResult = finalResult ? {
            success: (finalResult as any).success,
            eTag: (finalResult as any).eTag,
            operationsApplied: (finalResult as any).operationsApplied,
            totalOperations: (finalResult as any).totalOperations,
            // Include minimal message if present
            message: (finalResult as any).message
          } : finalResult
          return plainResult
        },
        {
          loadingMessage: API_MESSAGES.LOADING.APPLY_PATCH,
          successMessage: API_MESSAGES.SUCCESS.APPLY_PATCH,
          errorMessage: API_MESSAGES.ERROR.APPLY_PATCH,
          notificationOptions: {
            source: { id: 'configuration-store', label: 'Configuration Store' }
          }
        }
      )

      // Force a post-patch refresh so preview & introspection are immediately current
      try {
        if (import.meta.env.DEV) console.debug('[config:save] performing full refresh after patch')
        // 1. Refresh resolved configuration (force) to get authoritative new ETag
        await refreshConfiguration(true)
        
        // Update lockState.eTag with the fresh ETag from the reloaded configuration
        if (editorState.value.lockState.isLocked && currentConfiguration.value && 'eTag' in currentConfiguration.value) {
          const freshETag = normalizeETag((currentConfiguration.value as any).eTag)
          if (freshETag) {
            editorState.value.lockState.eTag = freshETag
            if (import.meta.env.DEV) console.debug('[config:save] updated lockState.eTag after refresh:', freshETag)
          }
        }
        
        // 2. Reload preset configurations & schemas using the updated presetSummary
        if (currentConfiguration.value && 'presetSummary' in currentConfiguration.value) {
          await loadPresetConfigurations()
          await loadPresetSchemas()
        }
        // 3. Re-sync variant selections from committed summary to avoid stale mapping & ETag conflicts
        if (currentConfiguration.value && 'presetSummary' in currentConfiguration.value) {
          const summary: any[] = (currentConfiguration.value as any).presetSummary || []
          const refreshed: Record<string, VariantRef | null> = {}
          PRESET_LEVELS.value.forEach(l => {
            const match = summary.find(s => (s.presetType || s.PresetType || '').toString().toLowerCase() === l.key)
            refreshed[l.key] = match ? { presetId: match.presetId ?? match.PresetId, name: match.presetName ?? match.PresetName } : null
          })
          committedVariantsByLevel.value = safeClone(refreshed)
          selectedVariantsByLevel.value = safeClone(refreshed)
        }
        // 4. Refresh template diagnostics & introspection
        console.log('[config:save] Refreshing template diagnostics after save')
        await resolveTemplateDiagnostics().catch((err) => {
          console.warn('[config:save] Template diagnostics refresh failed:', err)
        })
        await loadTemplateIntrospection().catch(() => {/* handled internally */})
      } catch (refreshErr) {
        console.warn('Post-patch refresh failed', refreshErr)
      }

      // Exit edit mode after successful save
      editorState.value.editMode = false
      editorState.value.lockState = {
        isLocked: false
      }
      
      // Clear snapshots since we're exiting edit mode
      originalPresetConfigurations.value = []
      sectionsInEditMode.value.clear() // Clear all section edit states
      
    } catch (err: any) {
      error.value = err.message || 'Failed to save configuration'
      console.error('Configuration save error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Save changes for a specific section (preset or tenant)
   * This method filters operations to only the specified section and saves them
   * 
   * @param sectionOperations - The patch operations for this specific section
   */
  const saveSectionChanges = async (sectionOperations: JsonPatchOperation[]) => {
    if (!editorState.value.editMode) {
      throw new Error('Cannot save: not in edit mode')
    }
    
    if (sectionOperations.length === 0) {
      throw new Error('No pending operations to save for this section')
    }
    
    loading.value = true
    error.value = null
    
    try {
      await withApiNotifications(
        async () => {
          // Group operations by preset or tenant
          const presetOperations = sectionOperations.filter(op => op.presetContext)
          const tenantOperations = sectionOperations.filter(op => !op.presetContext)
          
          let finalResult: any = null
          
          // Process preset operations if any
          if (presetOperations.length > 0) {
            // Group preset operations by presetId
            const groupedByPreset = presetOperations.reduce((groups, op) => {
              const presetId = op.presetContext!.presetId
              if (!groups[presetId]) {
                groups[presetId] = []
              }
              // Remove presetContext from the operation for the API call
              const { presetContext, ...cleanOp } = op
              groups[presetId].push(cleanOp)
              return groups
            }, {} as Record<number, JsonPatchOperation[]>)
            
            // Apply patches to each preset
            for (const [presetId, operations] of Object.entries(groupedByPreset)) {
              const presetIdNum = parseInt(presetId)
              const presetETag = presetETags.value[presetIdNum]
              
              // Validate that this preset was successfully loaded
              const presetExists = presetConfigurations.value.some(p => p.presetInfo.presetId === presetIdNum)
              if (!presetExists) {
                console.error(`❌ Cannot patch preset ${presetIdNum}: preset was not successfully loaded`)
                throw new Error(`Cannot save changes to preset ${presetIdNum}: preset configuration was not loaded successfully`)
              }
              
              if (!presetETag) {
                console.error(`❌ Cannot patch preset ${presetIdNum}: no ETag available`)
                throw new Error(`Cannot save changes to preset ${presetIdNum}: no ETag available for optimistic concurrency`)
              }
              
              if (import.meta.env.DEV) {
                console.log(`🟢 PATCH section preset ${presetIdNum}:`, JSON.stringify(operations, null, 2))
              }
              
              const result = await configurationService.applyPresetPatch(
                presetIdNum,
                operations,
                presetETag  // Use preset-specific ETag
              )
              
              if (!result.success) {
                throw new Error(`Preset ${presetId} patch failed: ${result.errors?.[0]?.message || 'Unknown error'}`)
              }
              
              // Update the stored ETag for this preset
              if (result.eTag) {
                presetETags.value[presetIdNum] = result.eTag
              }
              
              finalResult = result
            }
          }
          
          // Process tenant operations if any
          if (tenantOperations.length > 0 && editorState.value.selectedTenant) {
            if (import.meta.env.DEV) {
              console.log('� Sending section tenant operations:', tenantOperations)
            }
            
            const result = await configurationService.applyPatch(
              editorState.value.selectedTenant,
              tenantOperations,
              editorState.value.lockState.eTag
            )
            
            // Check for preset assignment operation failures
            if (result.presetOperations && result.presetOperations.length > 0) {
              const failedOps = result.presetOperations.filter((op: PresetPatchOperation) => !op.success)
              if (failedOps.length > 0) {
                console.error('❌ Section preset assignment operations failed:', failedOps)
                const errorMessages = failedOps.map((op: PresetPatchOperation) => 
                  `${op.operation.path}: ${op.presetName === 'assignment-error' ? 'Assignment error' : op.presetName}`
                ).join(', ')
                throw new Error(`Preset assignment failed: ${errorMessages}`)
              }
            }
            
            if (!result.success) {
              throw new Error(result.message || 'Tenant patch failed')
            }
            
            if (result.configuration) {
              currentConfiguration.value = result.configuration
              originalConfiguration.value = safeClone(result.configuration)
            }
            
            // Update ETag
            if (result.eTag) {
              editorState.value.lockState.eTag = result.eTag
            }
            
            finalResult = result
          }
          
          // Remove saved operations from pending
          pendingOperations.value = pendingOperations.value.filter(
            op => !sectionOperations.includes(op)
          )
          
          // Update dirty state
          editorState.value.isDirty = pendingOperations.value.length > 0
          
          // Reload configurations to reflect changes
          if (presetOperations.length > 0) {
            await loadPresetConfigurations()
          }
          
          return finalResult
        },
        {
          loadingMessage: 'Saving section changes...',
          successMessage: 'Section changes saved successfully',
          errorMessage: 'Failed to save section changes',
          notificationOptions: {
            source: { id: 'configuration-store', label: 'Configuration Store' }
          }
        }
      )
      
      // Before reloading, identify which presets have unsaved edits
      const savedPresetOperations = sectionOperations.filter(op => op.presetContext)
      const savedTenantOperations = sectionOperations.filter(op => !op.presetContext)
      
      // Find all presets that have pending operations (unsaved edits)
      const presetsWithUnsavedEdits = new Set<number>()
      pendingOperations.value.forEach(op => {
        if (op.presetContext) {
          presetsWithUnsavedEdits.add(op.presetContext.presetId)
        }
      })
      
      // Preserve current state of presets with unsaved edits before reload
      const preservedPresetStates = new Map<number, any>()
      presetsWithUnsavedEdits.forEach(presetId => {
        const preset = presetConfigurations.value.find(p => p.presetInfo.presetId === presetId)
        if (preset) {
          preservedPresetStates.set(presetId, safeClone(preset))
        }
      })
      
      if (savedPresetOperations.length > 0) {
        // Reload all preset configurations from API
        await loadPresetConfigurations()
        
        // Restore the state of presets with unsaved edits
        preservedPresetStates.forEach((preservedState, presetId) => {
          const currentIndex = presetConfigurations.value.findIndex(p => p.presetInfo.presetId === presetId)
          if (currentIndex >= 0) {
            // Restore the preserved state (which includes unsaved edits)
            presetConfigurations.value[currentIndex] = preservedState
          }
        })
        
        // Update snapshots for the saved presets only
        const groupedByPreset = savedPresetOperations.reduce((groups, op) => {
          const presetId = op.presetContext!.presetId
          groups[presetId] = true
          return groups
        }, {} as Record<number, boolean>)
        
        // For each saved preset, update its snapshot
        for (const presetIdStr of Object.keys(groupedByPreset)) {
          const presetId = parseInt(presetIdStr)
          const currentPreset = presetConfigurations.value.find(p => p.presetInfo.presetId === presetId)
          const snapshotIndex = originalPresetConfigurations.value.findIndex(p => p.presetInfo.presetId === presetId)
          
          if (currentPreset && snapshotIndex >= 0) {
            // Update only this preset's snapshot
            originalPresetConfigurations.value[snapshotIndex] = safeClone(currentPreset)
          }
        }
      }
      
      if (savedTenantOperations.length > 0) {
        // For tenant saves, also refresh the resolved configuration to get the latest state
        await refreshConfiguration(true)
        
        // Update lockState.eTag with the fresh ETag from the reloaded configuration
        if (editorState.value.lockState.isLocked && currentConfiguration.value && 'eTag' in currentConfiguration.value) {
          const freshETag = normalizeETag((currentConfiguration.value as any).eTag)
          if (freshETag) {
            editorState.value.lockState.eTag = freshETag
            if (import.meta.env.DEV) console.debug('[config:saveSectionChanges] updated lockState.eTag after refresh:', freshETag)
          }
        }
        
        // Update tenant snapshot
        if (currentConfiguration.value) {
          originalConfiguration.value = safeClone(currentConfiguration.value)
        }
        
        // Refresh template diagnostics after tenant save
        console.log('[config:saveSectionChanges] Refreshing template diagnostics after tenant save')
        await resolveTemplateDiagnostics().catch((err) => {
          console.warn('[config:saveSectionChanges] Template diagnostics refresh failed:', err)
        })
      }
      
    } catch (err: any) {
      error.value = err.message || 'Failed to save section configuration'
      console.error('Section configuration save error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get the section key for tracking edit state
   */
  const getSectionKey = (sectionType: 'preset' | 'tenant', presetId?: number): string => {
    return sectionType === 'preset' ? `preset:${presetId}` : 'tenant'
  }

  /**
   * Mark a section as being in edit mode
   */
  const setSectionEditMode = (sectionType: 'preset' | 'tenant', presetId: number | undefined, inEditMode: boolean) => {
    const key = getSectionKey(sectionType, presetId)
    if (inEditMode) {
      sectionsInEditMode.value.add(key)
    } else {
      sectionsInEditMode.value.delete(key)
    }
  }

  /**
   * Check if a specific section is in edit mode
   */
  const isSectionInEditMode = (sectionType: 'preset' | 'tenant', presetId?: number): boolean => {
    const key = getSectionKey(sectionType, presetId)
    return sectionsInEditMode.value.has(key)
  }

  /**
   * Check if a section has any editable fields for the current user
   * This walks through the configuration and checks if any field can be edited
   */
  const sectionHasEditableFields = (sectionType: 'preset' | 'tenant', presetId?: number): boolean => {
    const resource = sectionType === 'preset' ? 'presets' : 'tenants'
    
    // Check for blanket write permission first
    if (permissions.canWrite(resource)) {
      return true
    }
    
    // Get the configuration for this section
    let configuration: any = null
    if (sectionType === 'preset' && presetId !== undefined) {
      const preset = presetConfigurations.value.find(p => p.presetInfo.presetId === presetId)
      configuration = preset?.baseConfiguration || preset?.effectiveConfiguration
    } else if (sectionType === 'tenant') {
      // Get tenant configuration from the resolved config
      if (currentConfiguration.value && 'tenantConfiguration' in currentConfiguration.value) {
        configuration = (currentConfiguration.value as any).tenantConfiguration
      } else if (currentConfiguration.value && 'TenantConfiguration' in currentConfiguration.value) {
        configuration = (currentConfiguration.value as any).TenantConfiguration
      }
    }
    
    // If no configuration yet, check if user has any write scopes for this resource
    if (!configuration) {
      return permissions.scopes.value.some((scope: string) => {
        const parts = scope.split(':')
        if (parts.length < 2) return false
        const [scopeResource, scopeOperation] = parts
        return (scopeResource === resource || scopeResource === '*') &&
               (scopeOperation === 'write' || scopeOperation === '*')
      })
    }
    
    // Walk through configuration and check if any field is editable
    function hasEditableField(obj: any, prefix = ''): boolean {
      if (!obj || typeof obj !== 'object') return false
      
      for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key
        
        // Check if user can edit this specific path
        if (fieldPermissions.canEditField(resource, path)) {
          return true
        }
        
        // Recursively check nested objects (but not arrays)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          if (hasEditableField(value, path)) {
            return true
          }
        }
      }
      
      return false
    }
    
    return hasEditableField(configuration)
  }

  /**
   * Restore a single section (preset or tenant) to its original state
   * This is used when canceling edits for a specific section
   * 
   * @param sectionType - 'preset' or 'tenant'
   * @param presetId - The preset ID if sectionType is 'preset'
   */
  const restoreSectionFromSnapshot = (sectionType: 'preset' | 'tenant', presetId?: number) => {
    if (sectionType === 'preset' && presetId !== undefined) {
      // Find the original preset configuration from snapshot
      const originalPreset = originalPresetConfigurations.value.find(
        p => p.presetInfo.presetId === presetId
      )
      
      if (originalPreset) {
        // Find the current preset configuration and restore it
        const currentPresetIndex = presetConfigurations.value.findIndex(
          p => p.presetInfo.presetId === presetId
        )
        
        if (currentPresetIndex >= 0) {
          // Deep clone to avoid reference issues
          presetConfigurations.value[currentPresetIndex] = safeClone(originalPreset)
        }
      }
    } else if (sectionType === 'tenant') {
      // Restore tenant configuration from original snapshot
      if (originalConfiguration.value) {
        currentConfiguration.value = safeClone(originalConfiguration.value)
      }
    }
  }

  const setCurrentView = (view: 'json' | 'form' | 'preview') => {
    editorState.value.currentView = view
  }

  const refreshConfiguration = async (force: boolean = false) => {
    if (!editorState.value.selectedTenant) return
    // Re-fetch resolved configuration optionally bypassing cache via cache-buster param
    if (force) {
      loading.value = true
      try {
        const prevETag = (currentConfiguration.value as any)?.eTag
        const startedAt = performance.now()
        if (import.meta.env.DEV) console.debug('[config:refreshConfiguration] FORCE start', {
          tenant: editorState.value.selectedTenant,
          prevETag,
          time: new Date().toISOString()
        })
        const config = await configurationService.getResolvedConfiguration(
          editorState.value.selectedTenant,
          undefined,
          true,
          true // forceReload
        )
        currentConfiguration.value = config
        originalConfiguration.value = structuredClone(config)
        const newETag = (config as any)?.eTag
        if (import.meta.env.DEV) {
          const duration = (performance.now() - startedAt).toFixed(1)
          // Shallow diff top-level keys if configuration object present
          let keyDelta: any = undefined
          try {
            if (config && 'configuration' in config) {
              const oldKeys = prevETag && currentConfiguration.value ? Object.keys((currentConfiguration.value as any).configuration || {}) : []
              const newKeys = Object.keys((config as any).configuration || {})
              keyDelta = { oldCount: oldKeys.length, newCount: newKeys.length }
            }
          } catch {}
          console.debug('[config:refreshConfiguration] FORCE complete', {
            tenant: editorState.value.selectedTenant,
            prevETag,
            newETag,
            etagChanged: prevETag !== newETag,
            keyDelta,
            durationMs: duration
          })
        }
      } catch (e:any) {
        console.warn('Force refresh configuration failed', e)
      } finally {
        loading.value = false
      }
    } else {
      await loadConfiguration()
    }
  }

  // Force refresh ignoring any locally cached state (still leverages backend ETag semantics)
  const forceRefreshConfiguration = async () => {
    if (import.meta.env.DEV) console.debug('[config:forceRefreshConfiguration] invoked')
    await refreshConfiguration(true)
    await loadTemplateIntrospection().catch(() => {/* swallow */})
    if (import.meta.env.DEV) console.debug('[config:forceRefreshConfiguration] done')
  }

  const clearCache = async () => {
    if (!editorState.value.selectedTenant) return
    
    try {
      await withApiNotifications(
        async () => {
          await configurationService.clearTenantCache(editorState.value.selectedTenant!)
          await loadConfiguration()
        },
        {
          loadingMessage: API_MESSAGES.LOADING.CLEAR_CACHE,
          successMessage: API_MESSAGES.SUCCESS.CLEAR_CACHE,
          errorMessage: API_MESSAGES.ERROR.CLEAR_CACHE,
          notificationOptions: {
            source: { id: 'cache-management', label: 'Cache Management' }
          }
        }
      )
    } catch (err: any) {
      error.value = err.message || 'Failed to clear cache'
      console.error('Cache clear error:', err)
    }
  }

  // --- Variant selection actions ---
  const loadVariantsForLevel = async (levelKey: string) => {
    const level = PRESET_LEVELS.value.find(l => l.key === levelKey)
    if (!level) return
    if (fullyLoadedLevels.value[levelKey]) return // already fully loaded
    if (loadingVariants.value[levelKey]) return
    loadingVariants.value[levelKey] = true
    variantErrors.value[levelKey] = null
    try {
      const list = await configurationService.getPresetsByType(level.presetTypeName)
      availableVariantsByLevel.value[levelKey] = (list || []).map((p: any) => ({ presetId: p.id, name: p.name }))
      fullyLoadedLevels.value[levelKey] = true
    } catch (e: any) {
      variantErrors.value[levelKey] = e.message || 'Failed to load variants'
    } finally {
      loadingVariants.value[levelKey] = false
    }
  }

  const loadAllVariants = async () => {
    // Load all mutable variant levels in parallel to avoid dropdown closing issues
    const loadPromises = PRESET_LEVELS.value
      .filter(l => l.mutable)
      .map(l => loadVariantsForLevel(l.key))
    await Promise.all(loadPromises)
  }

  const selectVariantForLevel = (levelKey: string, presetId: number | null) => {
    const current = selectedVariantsByLevel.value[levelKey]?.presetId || null
    if (current === presetId) return
    if (presetId == null) {
      selectedVariantsByLevel.value[levelKey] = null
    } else {
      const list = availableVariantsByLevel.value[levelKey] || []
      const found = list.find(v => v.presetId === presetId)
      if (!found) return
      selectedVariantsByLevel.value[levelKey] = { presetId: found.presetId, name: found.name }
    }
    // Recompute dirty state
    editorState.value.isDirty = editorState.value.isDirty || variantDirty.value

    // If in edit mode, dynamically load the newly selected preset configuration so user can edit it immediately
    if (editorState.value.editMode && presetId != null) {
      const previousPresetId = current
      // Fire and forget; internal function handles async sequencing
      loadPresetConfigurationForVariant(levelKey, presetId, previousPresetId)
    }
  }

  // Track in-flight variant loads to avoid race conditions (last selection wins)
  const variantLoadTokens: Record<string, number> = {}
  const loadPresetConfigurationForVariant = async (levelKey: string, presetId: number, previousPresetId: number | null) => {
    if (!editorState.value.selectedTenant) return
    const token = (variantLoadTokens[levelKey] || 0) + 1
    variantLoadTokens[levelKey] = token
    try {
      // Fetch only the global preset; backend does not yet support previewing unassigned preset in tenant context.
      const globalPreset: any = await configurationService.getPreset(presetId.toString())
      // Abort if a newer selection started
      if (variantLoadTokens[levelKey] !== token) return
      // Fabricate a minimal PresetConfiguration-like object expected by UI.
      // Use the raw preset JSON as both baseConfiguration and effectiveConfiguration.
      const parsedJson = (() => { try { return JSON.parse(globalPreset.json || '{}') } catch { return {} } })()
      const newConfig: any = {
        presetInfo: {
          presetId: globalPreset.id,
          presetName: globalPreset.name,
          presetType: PRESET_LEVELS.value.find(l => l.key === levelKey)?.key || levelKey,
          description: globalPreset.description || '',
          hasTenantOverrides: false
        },
        baseConfiguration: parsedJson,
        inheritedConfiguration: {},
        tenantOverrides: {},
        effectiveConfiguration: parsedJson,
        templateVariables: [],
        changesSummary: { addedProperties: [], modifiedProperties: [], removedProperties: [], conflictsResolved: [] },
        resolvedAt: new Date().toISOString(),
        eTag: (globalPreset as any).eTag || globalPreset.ETag || '"' + (globalPreset.id + ':' + Date.now()) + '"'
      }
      // Remove pending operations targeting previous preset for this level to avoid accidental patches on deselected preset
      if (previousPresetId != null) {
        const before = pendingOperations.value.length
        pendingOperations.value = pendingOperations.value.filter(op => op.presetContext?.presetId !== previousPresetId)
        if (before !== pendingOperations.value.length && import.meta.env.DEV) {
          console.debug(`[variant] cleared ${before - pendingOperations.value.length} pending ops for old preset ${previousPresetId}`)
        }
        delete presetETags.value[previousPresetId]
      }
      // Insert or replace in presetConfigurations by presetType (levelKey)
      const idx = presetConfigurations.value.findIndex(pc => pc.presetInfo.presetType.toLowerCase() === levelKey.toLowerCase())
      let finalIndex = idx
      if (idx >= 0) {
        presetConfigurations.value[idx] = newConfig
      } else {
        // Insert at correct position based on precedence to maintain hierarchical order
        const newPrecedence = PRESET_LEVELS.value.find(l => l.key === levelKey)?.precedence ?? 999
        const insertIndex = presetConfigurations.value.findIndex(pc => {
          const pcType = pc.presetInfo.presetType.toLowerCase()
          const pcPrecedence = PRESET_LEVELS.value.find(l => l.key === pcType)?.precedence ?? 999
          return pcPrecedence > newPrecedence
        })
        if (insertIndex >= 0) {
          presetConfigurations.value.splice(insertIndex, 0, newConfig)
          finalIndex = insertIndex
        } else {
          presetConfigurations.value.push(newConfig)
          finalIndex = presetConfigurations.value.length - 1
        }
      }
      
      // Select the newly loaded preset and trigger scroll
      selectedPresetIndex.value = finalIndex
      isTenantOverridesSelected.value = false
      
      // Trigger scroll to the newly selected preset after DOM updates
      requestAnimationFrame(() => {
        const target = document.querySelector<HTMLDivElement>(`#preset-section-${finalIndex}`)
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
        }
      })
      
      // Update ETag mapping
  presetETags.value[presetId] = normalizeETag(newConfig.eTag) as string
  presetETags.value[presetId] = normalizeETag(newConfig.eTag) as string
      // Optimistically update currentConfiguration.presetSummary entry if present
      if (currentConfiguration.value && 'presetSummary' in currentConfiguration.value) {
        const summary: any[] = (currentConfiguration.value as any).presetSummary || []
        const matchIdx = summary.findIndex(s => (s.presetType || s.PresetType)?.toString?.().toLowerCase?.() === levelKey)
        if (matchIdx >= 0) {
          summary[matchIdx] = { ...summary[matchIdx], presetId, presetName: selectedVariantsByLevel.value[levelKey]?.name }
        } else {
          summary.push({ presetId, presetName: selectedVariantsByLevel.value[levelKey]?.name, presetType: levelKey, priority: 0, hasOverrides: false })
        }
        ;(currentConfiguration.value as any).presetSummary = summary
      }
    } catch (e) {
      console.warn(`[variant] failed to load preset configuration for ${levelKey}:${presetId}`, e)
    }
  }

  const createVariantForLevel = async (levelKey: string, name: string, description?: string) => {
    const level = PRESET_LEVELS.value.find(l => l.key === levelKey)
    if (!level || !level.mutable) return
    creatingVariant.value = { levelKey, inProgress: true }
    try {
      // Basic uniqueness check client-side
      const existing = (availableVariantsByLevel.value[levelKey] || []).some(v => v.name.toLowerCase() === name.toLowerCase())
      if (existing) throw new Error('Name already exists')
      const request = { Name: name, Description: description, PresetType: level.presetTypeName, Json: {} }
      const created = await withApiNotifications(
        () => configurationService.createPreset(request),
        {
          loadingMessage: undefined,
          successMessage: `Variant '${name}' created`,
          showSuccess: true,
          errorMessage: 'Failed to create variant',
          notificationOptions: { source: { id: 'variant-create', label: 'Variants' } }
        }
      )
      // Optimistically set selection immediately so user sees it
      const optimisticRef = { presetId: created.id, name: created.name }
      availableVariantsByLevel.value[levelKey] = [...(availableVariantsByLevel.value[levelKey] || []), optimisticRef]
      selectedVariantsByLevel.value[levelKey] = optimisticRef
      
      // Load the newly created preset configuration and scroll to it
      await loadPresetConfigurationForVariant(levelKey, created.id, null)
      
      // Background refresh to pick up any server-side normalization; update selection if canonical version differs
      const canonical = await refreshVariantsForLevel(levelKey, created.id)
      if (canonical) {
        selectedVariantsByLevel.value[levelKey] = canonical
      }
      editorState.value.isDirty = true
    } catch (e: any) {
      creatingVariant.value = { levelKey, inProgress: false, error: e.message || 'Failed to create variant' }
      return
    }
    creatingVariant.value = { levelKey: null, inProgress: false }
  }

  // Force reload variants list for a level (ignores existing cache) with small retry window for eventual consistency
  const refreshVariantsForLevel = async (levelKey: string, createdId?: number): Promise<VariantRef | undefined> => {
    const level = PRESET_LEVELS.value.find(l => l.key === levelKey)
    if (!level) return
    let attempts = 0
    let lastError: any = null
    while (attempts < 3) {
      try {
        const list = await configurationService.getPresetsByType(level.presetTypeName)
        let newList: VariantRef[] = (list || []).map((p: any) => ({ presetId: p.id, name: p.name }))
        if (createdId != null) {
          const existingCreated = availableVariantsByLevel.value[levelKey]?.find(v => v.presetId === createdId)
          const hasInNew = newList.some(v => v.presetId === createdId)
            // If backend doesn't yet return the new variant, keep optimistic one in list so user still sees it
          if (!hasInNew && existingCreated) {
            newList = [...newList, existingCreated]
          }
        }
        // De-duplicate by presetId
        newList = newList.filter((v, i, arr) => arr.findIndex(x => x.presetId === v.presetId) === i)
        availableVariantsByLevel.value[levelKey] = newList
        if (createdId != null) {
          const match = newList.find(v => v.presetId === createdId)
          if (match) return match // Found canonical (or optimistic) entry
        } else {
          return undefined
        }
      } catch (e: any) {
        lastError = e
      }
      attempts++
      if (createdId != null) {
        // Short backoff for eventual consistency
        await new Promise(r => setTimeout(r, 150 * attempts))
      }
    }
    if (lastError) {
      console.warn('[variants] refresh failed after retries', lastError)
    }
    // Fallback: ensure created item remains listed if we never saw it in refresh
    if (createdId != null && !availableVariantsByLevel.value[levelKey]?.some(v => v.presetId === createdId)) {
      const sel = selectedVariantsByLevel.value[levelKey]
      if (sel && sel.presetId === createdId) {
        availableVariantsByLevel.value[levelKey] = [...(availableVariantsByLevel.value[levelKey] || []), sel]
        return sel
      }
    }
    return undefined
  }

  const resetVariantSelections = () => {
    selectedVariantsByLevel.value = structuredClone(committedVariantsByLevel.value)
  }

  const loadTemplateIntrospection = async (maxDepth: number = 10) => {
    if (!editorState.value.selectedTenant) return
    loadingIntrospection.value = true
    introspectionError.value = null
    try {
      templateIntrospection.value = await configurationService.getTemplateIntrospection(editorState.value.selectedTenant, maxDepth)
    } catch (e: any) {
      introspectionError.value = e.message || 'Failed to load template introspection'
      console.error('Template introspection load error:', e)
    } finally {
      loadingIntrospection.value = false
    }
  }

  const setSelectedPresetIndex = (index: number) => {
    selectedPresetIndex.value = index
    isTenantOverridesSelected.value = false
  }

  /**
   * Set tenant overrides selected state and deselect preset
   */
  const setTenantOverridesSelected = (selected: boolean) => {
    isTenantOverridesSelected.value = selected
    if (selected) {
      selectedPresetIndex.value = -1
    }
  }

  return {
    // State
    editorState,
    selectedPresetIndex,
    isTenantOverridesSelected,
    currentConfiguration,
    originalConfiguration,
    pendingOperations,
    loading,
    error,
    
    // Preset configurations state
    presetConfigurations,
    loadingPresets,
    presetErrors,
    presetETags,
    
    // Schema state
    availableSchemas,
    presetSchemas,
    loadingSchemas,
    schemaErrors,
    
    // Computed
    isEditMode,
    isLocked,
    isDirty,
    hasValidationErrors,
    activePresetsCount,
    hasPresetErrors,
    presetHierarchy,
    configurationJson,
    canEditPresets,
    canEditTenants,
    canEditCurrentResource,
    isReadOnlyMode,
    
    // Global view mode for sections
    globalSectionViewMode,
    globalSectionViewModeForceSync,
    updateSectionViewMode,
    removeSectionViewMode,
    
    // Actions
    setSelectedTenant,
    setSelectedPreset,
    setSelectedPresetIndex,
    setTenantOverridesSelected,
    loadConfiguration,
  resolveTemplateDiagnostics,
    loadPresetConfigurations,
    loadAvailableSchemas,
    loadPresetSchemas,
    enterEditMode,
    exitEditMode,
    updateConfiguration,
    addPatchOperation,
    saveChanges,
    saveSectionChanges,
    restoreSectionFromSnapshot,
    setSectionEditMode,
    isSectionInEditMode,
    hasAnySectionsInEditMode,
    sectionHasEditableFields,
  setCurrentView,
    refreshConfiguration,
  forceRefreshConfiguration,
    clearCache
    ,loadTemplateIntrospection
    ,templateIntrospection
    ,loadingIntrospection
    ,introspectionError
    // Diagnostics
    ,templateDiagnosticsErrors
    ,templateDiagnosticsErrorCount
    ,templateDiagnosticsLastUpdated
    ,setTemplateDiagnosticsErrors
    // Variant selection
    ,PRESET_LEVELS
    ,loadPrecedenceLevels
    ,loadRoles
    ,availableVariantsByLevel
    ,selectedVariantsByLevel
    ,committedVariantsByLevel
    ,loadingVariants
    ,variantErrors
    ,creatingVariant
    ,variantDirty
    ,loadVariantsForLevel
    ,selectVariantForLevel
  ,createVariantForLevel
  ,refreshVariantsForLevel
    ,resetVariantSelections
    // Permissions
    ,permissions
    ,fieldPermissions
  }
})
